import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import LoadingButton from '../components/LoadingButton';
import { apiService } from '../services/api';
import {
  CreditPackage,
  CreditPackageKind,
  CreditPackageUpdate
} from '../types/creditPackage';

// Catálogo de PAQUETES DE CRÉDITOS: precios y textos comerciales del producto
// «empleados IA». Existe para que las dos cosas que más se mueven —cuánto
// cuesta y cómo se cuenta— se cambien sin tocar código ni redesplegar.
//
// El campo clave es «Para qué vale»: es lo que hace comprensible un paquete
// («un empleado con uso tranquilo: ~25 tareas autónomas al mes»). Se escribe
// aquí a propósito y NO se calcula: la equivalencia créditos↔trabajo depende
// del empleado, del modelo y del uso real, así que es una promesa comercial.
//
// No confundir con «Planes» de facturación (tabla `plans`): esas son las
// suscripciones B2C de TENANTS por Paddle, otro producto que sigue vivo.

const formatUsd = (value: number | string | null | undefined): string => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? String(value ?? '') : num.toFixed(2);
};

const formatCredits = (value: number | string | null | undefined): string => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? String(value ?? '') : num.toLocaleString();
};

interface FormState {
  kind: CreditPackageKind;
  name: string;
  credits: string;
  price_usd: string;
  what_for: string;
  sort_order: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  kind: 'plan',
  name: '',
  credits: '',
  price_usd: '',
  what_for: '',
  sort_order: '0',
  is_active: true
};

const CreditPackagesPage: React.FC = () => {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CreditPackage | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<CreditPackage | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      // Incluye los retirados: son los que el admin puede reactivar.
      setPackages(await apiService.getCreditPackages(false));
    } catch (err: any) {
      console.error('Failed to load credit packages:', err);
      setError(err?.detail || t('creditPackages.loadError', 'No se pudo cargar el catálogo'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (pkg: CreditPackage) => {
    setEditing(pkg);
    setForm({
      kind: (pkg.kind as CreditPackageKind) || 'plan',
      name: pkg.name,
      credits: String(Number(pkg.credits)),
      price_usd: String(Number(pkg.price_usd)),
      what_for: pkg.what_for || '',
      sort_order: String(pkg.sort_order ?? 0),
      is_active: pkg.is_active
    });
    setFormError('');
    setDialogOpen(true);
  };

  const credits = Number(form.credits);
  const price = Number(form.price_usd);
  const formValid =
    form.name.trim() !== '' &&
    form.credits.trim() !== '' && !Number.isNaN(credits) && credits > 0 &&
    form.price_usd.trim() !== '' && !Number.isNaN(price) && price >= 0;

  // Precio unitario en vivo: es la cifra con la que se compara un paquete con
  // otro, y verla al escribir evita publicar un «grande» más caro por crédito
  // que el pequeño — el error que rompe todo el argumento de volumen.
  const perCredit = formValid && credits > 0 ? price / credits : null;

  const handleSave = async () => {
    if (!formValid) return;
    try {
      setSaving(true);
      setFormError('');
      if (editing) {
        // `kind` NO viaja: no se puede cambiar (plan y extra viven en bolsas
        // distintas del ledger del cliente).
        const payload: CreditPackageUpdate = {
          name: form.name.trim(),
          credits,
          price_usd: price,
          what_for: form.what_for.trim(),
          sort_order: Number(form.sort_order) || 0,
          is_active: form.is_active
        };
        await apiService.updateCreditPackage(editing.id, payload);
        toast.success(t('creditPackages.updated', 'Paquete actualizado'));
      } else {
        await apiService.createCreditPackage({
          kind: form.kind,
          name: form.name.trim(),
          credits,
          price_usd: price,
          what_for: form.what_for.trim(),
          sort_order: Number(form.sort_order) || 0,
          is_active: form.is_active
        });
        toast.success(t('creditPackages.created', 'Paquete creado'));
      }
      setDialogOpen(false);
      load();
    } catch (err: any) {
      console.error('Failed to save credit package:', err);
      setFormError(err?.detail || t('creditPackages.saveError', 'No se pudo guardar el paquete'));
    } finally {
      setSaving(false);
    }
  };

  // Retirar/reactivar sin abrir el diálogo: es el cambio más frecuente.
  const handleToggleActive = async (pkg: CreditPackage) => {
    try {
      await apiService.updateCreditPackage(pkg.id, { is_active: !pkg.is_active });
      load();
    } catch (err: any) {
      toast.error(err?.detail || t('creditPackages.saveError', 'No se pudo guardar el paquete'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await apiService.deleteCreditPackage(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err: any) {
      toast.error(err?.detail || t('creditPackages.deleteError', 'No se pudo borrar el paquete'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t('creditPackages.title', 'Paquetes de créditos')}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none' }}>
          {t('creditPackages.create', 'Nuevo paquete')}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('creditPackages.subtitle', 'Lo que compran los clientes de empleados IA: plan mensual (sus créditos caducan al cerrar el ciclo) y packs extra puntuales (no caducan). Precios y textos se editan aquí, sin tocar código.')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {packages.length === 0 ? (
        <Alert severity="info">
          {t('creditPackages.empty', 'No hay paquetes todavía. Crea el primero para que aparezca en el portal del cliente.')}
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('creditPackages.fields.kind', 'Tipo')}</TableCell>
                <TableCell>{t('creditPackages.fields.name', 'Nombre')}</TableCell>
                <TableCell align="right">{t('creditPackages.fields.credits', 'Créditos')}</TableCell>
                <TableCell align="right">{t('creditPackages.fields.price', 'Precio (USD)')}</TableCell>
                <TableCell align="right">{t('creditPackages.fields.perCredit', 'USD/crédito')}</TableCell>
                <TableCell>{t('creditPackages.fields.whatFor', 'Para qué vale')}</TableCell>
                <TableCell align="center">{t('creditPackages.fields.active', 'Activo')}</TableCell>
                <TableCell align="right">{t('common.actions', 'Acciones')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.id} hover sx={{ opacity: pkg.is_active ? 1 : 0.55 }}>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={pkg.kind === 'plan' ? 'primary' : 'default'}
                      label={pkg.kind === 'plan'
                        ? t('creditPackages.kind.plan', 'Plan mensual')
                        : t('creditPackages.kind.extra', 'Pack extra')}
                    />
                  </TableCell>
                  <TableCell>{pkg.name}</TableCell>
                  <TableCell align="right">{formatCredits(pkg.credits)}</TableCell>
                  <TableCell align="right">{formatUsd(pkg.price_usd)}</TableCell>
                  <TableCell align="right">{Number(pkg.price_per_credit ?? 0).toFixed(3)}</TableCell>
                  <TableCell sx={{ maxWidth: 340 }}>
                    <Typography variant="caption" color="text.secondary">
                      {pkg.what_for || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={pkg.is_active}
                      onChange={() => handleToggleActive(pkg)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('common.edit', 'Editar') as string}>
                      <Button size="small" onClick={() => openEdit(pkg)} sx={{ minWidth: 0 }}>
                        <EditIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                    <Tooltip title={t('creditPackages.delete', 'Borrar') as string}>
                      <Button size="small" color="error" onClick={() => setDeleteTarget(pkg)} sx={{ minWidth: 0 }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Alta / edición */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing
            ? t('creditPackages.editTitle', 'Editar paquete')
            : t('creditPackages.createTitle', 'Nuevo paquete')}
        </DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <FormControl fullWidth margin="dense" disabled={!!editing}>
            <InputLabel>{t('creditPackages.fields.kind', 'Tipo')}</InputLabel>
            <Select
              label={t('creditPackages.fields.kind', 'Tipo')}
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as CreditPackageKind })}
            >
              <MenuItem value="plan">{t('creditPackages.kind.plan', 'Plan mensual')}</MenuItem>
              <MenuItem value="extra">{t('creditPackages.kind.extra', 'Pack extra')}</MenuItem>
            </Select>
          </FormControl>
          {editing && (
            <Typography variant="caption" color="text.secondary">
              {t('creditPackages.kindLocked', 'El tipo no se puede cambiar: plan y extra viven en bolsas distintas del saldo del cliente. Retira este paquete y crea otro.')}
            </Typography>
          )}
          <TextField
            margin="dense"
            fullWidth
            label={t('creditPackages.fields.name', 'Nombre') + ' *'}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Box display="flex" gap={2}>
            <TextField
              margin="dense"
              fullWidth
              type="number"
              label={t('creditPackages.fields.credits', 'Créditos') + ' *'}
              value={form.credits}
              onChange={(e) => setForm({ ...form, credits: e.target.value })}
              inputProps={{ min: 1, step: '1' }}
            />
            <TextField
              margin="dense"
              fullWidth
              type="number"
              label={t('creditPackages.fields.price', 'Precio (USD)') + ' *'}
              value={form.price_usd}
              onChange={(e) => setForm({ ...form, price_usd: e.target.value })}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Box>
          {perCredit !== null && (
            <Typography variant="caption" color="text.secondary">
              {t('creditPackages.perCreditLive', 'Sale a {{price}} USD por crédito', {
                price: perCredit.toFixed(3)
              })}
            </Typography>
          )}
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={2}
            label={t('creditPackages.fields.whatFor', 'Para qué vale')}
            placeholder={t('creditPackages.whatForPlaceholder', 'Un empleado con uso tranquilo: unas 25 tareas autónomas al mes.')}
            helperText={t('creditPackages.whatForHelp', 'En lenguaje de negocio: es lo que el cliente lee para decidir. No se calcula solo.')}
            value={form.what_for}
            onChange={(e) => setForm({ ...form, what_for: e.target.value })}
          />
          <Box display="flex" alignItems="center" gap={2} mt={1}>
            <TextField
              margin="dense"
              type="number"
              label={t('creditPackages.fields.order', 'Orden')}
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              sx={{ width: 140 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label={t('creditPackages.fields.active', 'Activo') as string}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton onClick={handleSave} loading={saving} disabled={!formValid} variant="contained">
            {t('common.save', 'Guardar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Borrado físico: la vía normal para retirar es el interruptor «Activo». */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('creditPackages.deleteTitle', 'Borrar paquete')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t('creditPackages.deleteWarning', 'Se borra la fila para siempre. Si el paquete ya se ha ofrecido, es mejor desactivarlo: así queda el registro de a qué precio se vendió.')}
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {deleteTarget?.name}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton onClick={handleDelete} loading={deleting} color="error" variant="contained">
            {t('creditPackages.delete', 'Borrar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreditPackagesPage;
